// uikit.ts — cross-cutting UI constants + paint helpers extracted from form.ts
// (canvas-removal R3). These are foundational primitives the DOM views, the renderer,
// buildui, and the playfield layers depend on independently of the forms.ini Form
// mechanism, so they live in a neutral module that both form.ts and the survivors import.
//
//  - form/control flag + notify + selection constants (ht.h:2723-2951)
//  - the kidcOk/kidcCancel button ids (res.h:83-84), vchrBack, the GUI sfx indices
//  - PcFromFc (the form-coord scale, ht.h:849-851)
//  - the 4 paint helpers ShadowHelper/FillHelper/DrawBorder/BltHelper (form.cpp:695-760,
//    misc.cpp:1431-1450) — used by the playfield layers, the minimap, buildui's overlays.

import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { TBitmap } from '../gfx/tbitmap.ts';
import { ksideNeutral } from '../gfx/tbitmap.ts';
import type { UpdateMap } from '../gfx/updatemap.ts';
import type { Color } from '../gfx/texatlasmgr.ts';
import type { Font } from '../gfx/font.ts';
import { Rect } from './rect.ts';

// ht.h:2723-2735
export const kcControlsMax = 32;
export const kfFrmDeleted = 0x01;
export const kfFrmDoModal = 0x02;
export const kfFrmVisible = 0x04;
export const kfFrmPenInside = 0x08;
export const kfFrmScaleCoords = 0x40;
export const kfFrmAutoTakedown = 0x80;
export const kfFrmTranslucent = 0x100;
export const kfFrmNoFocus = 0x200;
export const kfFrmShowSound = 0x400;
export const kfFrmNoEcom = 0x800;
export const kfFrmTopMost = 0x1000;
export const kfFrmDemandPen2 = 0x2000;

// ht.h:2737-2738
export const knNotifySelectionChange = 0;
export const knNotifySelectionTap = 1;

// ht.h:2940-2945
export const knSelDownInside = 0;
export const knSelUpInside = 1;
export const knSelUpOutside = 2;
export const knSelMoveInside = 3;
export const knSelMoveOutside = 4;
export const knSelHoldInside = 5;

// ht.h:2947-2951
export const kfCtlVisible = 1;
export const kfCtlSet = 2;
export const kfCtlRedraw = 4;
export const kfCtlDisabled = 8;
export const kfCtlUseSide1Colors = 16;

// game/res.h:83-84
export const kidcOk = 2000;
export const kidcCancel = 2001;

// game/sdl/htplatform.h:42
export const vchrBack = 0x10c;

// game/soundeffects.h enum Sfx — order-derived indices (passed through as numbers)
export const ksfxNothing = 0;
export const ksfxGuiButtonTap = 29; // (was misccontrols.ts)
export const ksfxGuiCheckBoxTap = 30; // (was misccontrols.ts)
export const ksfxGuiFormHide = 32;
export const ksfxGuiFormShow = 33;
export const ksfxGuiScrollingListSelectItem = 35; // (was misccontrols.ts)

// ht.h:3120-3127 — LabelControl flag bits + the text buffer cap (was misccontrols.ts)
export const kfLblCenterText = 0x8000;
export const kfLblMultiLine = 0x4000;
export const kfLblRightText = 0x2000;
export const kfLblClipVertical = 0x1000;
export const kfLblEllipsis = 0x0800;
export const kfLblHitTest = 0x0400;
export const kcbLabelTextMax = 1000;

// game/res.h:76 (was misccontrols.ts)
export const kifntShadow = 1;

// ht.h:849-851 — form coords scale by the display width (game.cpp:1290-1308
// sets gcxFullScreenFormDiv10 = 16/24/32 for 160/240/320+ wide displays)
export function PcFromFc(fc: number, gcxFullScreenFormDiv10: number): number {
  return Math.trunc((fc * gcxFullScreenFormDiv10) / 16);
}

// game/form.cpp:695-715
export function ShadowHelper(pbm: CpuDibBitmap, pupd: UpdateMap | null, prc: Rect | null): void {
  if (prc === null) {
    const siz = pbm.GetSize();
    const rc = new Rect();
    rc.Set(0, 0, siz.cx, siz.cy);
    prc = rc;
  }

  if (pupd === null) {
    pbm.Shadow(prc.left, prc.top, prc.Width(), prc.Height());
  } else {
    let fFirst = true;
    for (;;) {
      const rcInvalid = pupd.EnumUpdateRects(fFirst, prc);
      if (rcInvalid === null) break;
      fFirst = false;
      pbm.Shadow(
        rcInvalid.left,
        rcInvalid.top,
        rcInvalid.right - rcInvalid.left,
        rcInvalid.bottom - rcInvalid.top,
      );
    }
  }
}

// game/form.cpp:717-737
export function FillHelper(
  pbm: CpuDibBitmap,
  pupd: UpdateMap | null,
  prc: Rect | null,
  clr: Color,
): void {
  if (prc === null) {
    const siz = pbm.GetSize();
    const rc = new Rect();
    rc.Set(0, 0, siz.cx, siz.cy);
    prc = rc;
  }

  if (pupd === null) {
    pbm.Fill(prc.left, prc.top, prc.Width(), prc.Height(), clr);
  } else {
    let fFirst = true;
    for (;;) {
      const rcInvalid = pupd.EnumUpdateRects(fFirst, prc);
      if (rcInvalid === null) break;
      fFirst = false;
      pbm.Fill(
        rcInvalid.left,
        rcInvalid.top,
        rcInvalid.right - rcInvalid.left,
        rcInvalid.bottom - rcInvalid.top,
        clr,
      );
    }
  }
}

// game/misc.cpp:1720-1764 — expands "{pvar}" references via ggame.GetVar (the
// getVar seam); an unterminated '{' is copied through literally. cbBuff truncation
// (kcbLabelTextMax for labels) preserved. (was misccontrols.ts)
export function ExpandVars(
  pszSrc: string,
  cbBuff: number,
  getVar: (name: string) => string,
): string {
  let out = '';
  let i = 0;
  let cb = cbBuff;
  while (i < pszSrc.length && cb > 1) {
    const ch = pszSrc[i++];

    // Variable?

    if (ch === '{') {
      // yes

      let szT = '';
      const iBeforeVar = i;
      while (i < pszSrc.length && pszSrc[i] !== '}' && szT.length < 32 /* kcbPvarNameMax */) {
        szT += pszSrc[i++];
      }

      if (i < pszSrc.length) {
        const v = getVar(szT).slice(0, Math.max(0, cb - 1));
        out += v;
        cb -= v.length;
        i++;
      } else {
        // Variable not terminated properly

        i = iBeforeVar;
        out += ch;
        cb--;
      }
    } else {
      // No, just copy it

      out += ch;
      cb--;
    }
  }
  return out;
}

// game/misc.cpp:1431-1450 — the 4-strip rect border (UI chrome: buttons,
// minimap view box, drag-selection rect)
export function DrawBorder(
  pbm: CpuDibBitmap,
  prc: Rect,
  nThickness: number,
  clr: Color,
  pupd: UpdateMap | null = null,
): void {
  if (pupd === null) {
    pbm.Fill(prc.left, prc.top, prc.Width(), nThickness, clr);
    pbm.Fill(prc.left, prc.top, nThickness, prc.Height(), clr);
    pbm.Fill(prc.right - nThickness, prc.top, nThickness, prc.Height(), clr);
    pbm.Fill(prc.left, prc.bottom - nThickness, prc.Width(), nThickness, clr);
  } else {
    const rcT = new Rect();
    rcT.Set(prc.left, prc.top, prc.right, prc.top + nThickness);
    FillHelper(pbm, pupd, rcT, clr);
    rcT.Set(prc.left, prc.top, prc.left + nThickness, prc.bottom);
    FillHelper(pbm, pupd, rcT, clr);
    rcT.Set(prc.right - nThickness, prc.top, prc.right, prc.bottom);
    FillHelper(pbm, pupd, rcT, clr);
    rcT.Set(prc.left, prc.bottom - nThickness, prc.right, prc.bottom);
    FillHelper(pbm, pupd, rcT, clr);
  }
}

// game/form.cpp:739-760
export function BltHelper(
  pbm: CpuDibBitmap,
  ptbm: TBitmap,
  pupd: UpdateMap | null,
  xDst: number,
  yDst: number,
): void {
  if (pupd === null) {
    ptbm.BltTo(pbm, xDst, yDst);
  } else {
    const siz = ptbm.GetSize();
    const rc = new Rect();
    rc.Set(xDst, yDst, xDst + siz.cx, yDst + siz.cy);
    let fFirst = true;
    for (;;) {
      const rcInvalid = pupd.EnumUpdateRects(fFirst, rc);
      if (rcInvalid === null) break;
      fFirst = false;
      const rcSrc = new Rect();
      rcSrc.left = rcInvalid.left - xDst;
      rcSrc.top = rcInvalid.top - yDst;
      rcSrc.right = rcSrc.left + (rcInvalid.right - rcInvalid.left);
      rcSrc.bottom = rcSrc.top + (rcInvalid.bottom - rcInvalid.top);
      ptbm.BltTo(pbm, rcInvalid.left, rcInvalid.top, ksideNeutral, rcSrc);
    }
  }
}

//
// FancyText (misccontrols.cpp:639-722) — relocated here (canvas-removal R3) so it
// survives the misccontrols.ts deletion; the UnitTitleLayer's title strip draws
// through it (buildui.ts UnitMenu.PaintTitleControls).
//
// FancyText supports special character sequences to control the formatting
// of the text as it is displayed ("@@" escapes '@'; "@S." side-color mapping
// is a parsed-but-TODO control sequence; the default font carries the symbol
// glyphs).
//

// game/misccontrols.cpp:652-655
export function DrawFancyText(
  pbm: CpuDibBitmap,
  pfntDefault: Font,
  psz: string,
  x: number,
  y: number,
  cch = 0,
): number {
  return FancyTextCore(pbm, pfntDefault, psz, x, y, cch, false);
}

// game/misccontrols.cpp:657-660
export function GetFancyTextExtent(pfntDefault: Font, psz: string, cch = 0): number {
  return FancyTextCore(null, pfntDefault, psz, 0, 0, cch, true);
}

// game/misccontrols.cpp:662-722 — the pointer walk becomes index arithmetic
// (psz = run start, pchT = scan cursor)
export function FancyTextCore(
  pbm: CpuDibBitmap | null,
  pfntDefault: Font,
  psz: string,
  x: number,
  y: number,
  cch: number,
  fGetExtent: boolean,
): number {
  const pclr: Color | null = null;
  const pfnt = pfntDefault;
  const s = psz;

  if (cch === 0) cch = s.length;

  let cx = 0;

  let cchT = cch;
  let pchT = 0; // index into s
  let pszRun = 0; // the C++ psz cursor (run start)
  while (cchT > 0) {
    const ch = s[pchT++];
    cchT--;

    if (ch === '@') {
      if (pchT !== pszRun + 1) {
        if (fGetExtent) {
          cx += pfnt.GetTextExtent(s.slice(pszRun), pchT - pszRun - 1);
        } else {
          cx += pfnt.DrawText(pbm!, s.slice(pszRun), x, y, pchT - pszRun - 1, pclr);
          x += cx;
        }
        pszRun = pchT;
      }

      switch (s[pchT]) {
        // "@@" outputs a single @
        case '@':
          cchT--;
          pszRun = pchT++;
          continue;

        // TODO: "@S." turns on mapping to local player's side color
        case 'S':
          cchT--;
          pchT++;
          break;
      }

      // Assert(s[pchT] == '.')

      // Skip past '.'
      cchT--;
      pchT++;
      pszRun = pchT;
    }
  }

  if (pchT !== pszRun) {
    if (fGetExtent) {
      cx += pfnt.GetTextExtent(s.slice(pszRun), pchT - pszRun);
    } else {
      cx += pfnt.DrawText(pbm!, s.slice(pszRun), x, y, pchT - pszRun, pclr);
    }
  }

  return cx;
}
